<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.permission_group') }}
              </span>
            </p>
            <b-select
              v-model="selectedPermission"
              :loading="loadingPermissions"
              :placeholder="
                $t('message.please_select') + $t('attributes.permission_group')
              "
              @input="onFilter"
            >
              <option value="">全部權限群組</option>
              <option
                v-for="option in permissionList"
                :value="option"
                :key="option.id"
              >
                {{ option.status ? '' : '[下架]' }}
                {{ option.title }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.depart') }}
              </span>
            </p>
            <b-select
              v-model="selectedDepart"
              :loading="loadingDepart"
              :placeholder="
                $t('message.please_select') + $t('attributes.depart')
              "
              @input="onFilter"
            >
              <option value="">全部部門</option>
              <option
                v-for="option in departs"
                :value="option"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.companies') }}
              </span>
            </p>
            <b-select
              v-model="selectedCompany"
              :loading="loadingCompany"
              :placeholder="
                $t('message.please_select') + $t('attributes.companies')
              "
              @input="onFilter"
            >
              <option value="">全部分公司</option>
              <option
                v-for="option in companies"
                :value="option"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                在職狀態
              </span>
            </p>
            <b-select
              v-model="selectedStatus"
              :placeholder="$t('message.please_select')"
              @input="onFilter"
            >
              <option value="">全部</option>
              <option value="1">在職</option>
              <option value="0">離職</option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilterSearch"
            ></b-input>
          </b-field>
          <b-field>
            <button class="button is-primary" @click="loadEmployees()">
              <span>{{ $t('attributes.search') }}</span>
            </button>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.employee') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="employee_code"
        :label="$t('attributes.employee_code')"
        width="110"
        sortable
        numeric
        v-slot="props"
      >
        {{ props.row.emp_code }}
      </b-table-column>
      <b-table-column
        field="name"
        :label="$t('attributes.employee_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>
      <b-table-column
        field="permission"
        :label="$t('attributes.permission_group')"
        sortable
        v-slot="props"
      >
        {{ props.row.permission_groups.map(p => p.title).join(', ') }}
      </b-table-column>
      <b-table-column
        field="department"
        :label="$t('attributes.depart')"
        sortable
        v-slot="props"
      >
        {{ props.row.department && props.row.department.name }}
      </b-table-column>
      <b-table-column
        field="company"
        :label="$t('attributes.companies')"
        sortable
        v-slot="props"
      >
        {{ props.row.company && props.row.company.name }}
      </b-table-column>
      <b-table-column
        field="status"
        :label="$t('attributes.employee_status')"
        sortable
        v-slot="props"
      >
        {{ employeeStatus(props.row.status) }}
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="330"
        centered
        v-slot="props"
      >
        <div class="buttons is-center">
          <b-tooltip
            label="修改使用者資料"
            type="is-light"
            position="is-bottom"
            :animated="false"
          >
            <b-button type="is-info" @click.prevent.stop="onModify(props.row)">
              {{ $t('attributes.modify') }}
            </b-button>
          </b-tooltip>
          <b-tooltip
            label="設定使用者的權限群組"
            type="is-light"
            position="is-bottom"
            :animated="false"
          >
            <b-button
              type="is-danger"
              @click.prevent.stop="onGroupsModify(props.row)"
            >
              {{ $t('attributes.group') }}
            </b-button>
          </b-tooltip>
          <b-tooltip
            label="設定可管理的線別"
            type="is-light"
            position="is-bottom"
            :animated="false"
          >
            <b-button
              type="is-warning"
              @click.prevent.stop="onAreaModify(props.row)"
            >
              {{ $t('attributes.area') }}
            </b-button>
          </b-tooltip>
          <b-tooltip
            label="寄送帳密資訊給使用者"
            type="is-light"
            position="is-bottom"
            :animated="false"
          >
            <b-button
              type="is-link"
              @click.prevent.stop="onSendMail(props.row)"
            >
              {{ $t('attributes.send_mail') }}
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal :active.sync="isModalActive" has-modal-card>
      <employee-form
        :sending="sending"
        :employee="selected"
        :companies="companies"
        :departs="departs"
        :positions="positions"
        :permission-list="permissionList"
        @onSend="onSend"
      />
    </b-modal>

    <b-modal :active.sync="isPermissionGroupModalActive" has-modal-card>
      <employee-groups-form
        :sending="sending"
        :employee="selected"
        @onSend="onUpdateGroups"
      />
    </b-modal>

    <b-modal :active.sync="isAreaModelActive" has-modal-card>
      <employee-area-form
        :sending="sending"
        :employee="selected"
        @onSend="onUpdateAreas"
      />
    </b-modal>
  </section>
</template>
<style>
.buttons button {
  margin: 5px;
}
</style>

<script>
import {
  DEPARTMENTS_API,
  POSITIONS_API,
  PERMISSION_GROUPS_API,
  COMPANIES_API,
  EMPLOYEES_API
} from '@/utils/apis'
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import EmployeeForm from '@/components/system/EmployeeForm'
import EmployeeGroupsForm from '@/components/system/EmployeeGroupsForm'
import EmployeeAreaForm from '@/components/system/EmployeeAreaForm'

export default {
  name: 'EmployeeList',
  components: {
    EmployeeGroupsForm,
    EmployeeAreaForm,
    EmployeeForm
  },
  data() {
    return {
      loading: false,

      loadingDepart: false,
      selectedDepart: '',
      departs: [],

      loadingPermissions: false,
      selectedPermission: '',
      permissionList: [],

      loadingCompany: false,
      selectedCompany: '',
      companies: [],

      loadingPosition: false,
      selectedPosition: '',
      positions: [],

      loadingEmployee: false,
      selected: null,
      items: [],

      isModalActive: false,
      isPermissionGroupModalActive: false,
      isAreaModelActive: false,

      selectedStatus: 1,
      searchName: '',
      total: 0,
      perPage: 10,
      page: 1,
      defaultSortOrder: 'asc',
      sortField: 'code',
      sortOrder: 'asc',

      sending: false,
      isNew: false
    }
  },
  computed: {
    isLoading() {
      return (
        this.loading ||
        this.loadingDepart ||
        this.loadingPermissions ||
        this.loadingCompany ||
        this.loadingPosition ||
        this.loadingEmployee
      )
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      await this.loadDepartment()
      await this.loadPermissionList()
      await this.loadCompany()
      await this.loadPosition()
      await this.loadEmployees()
      this.loading = false
    },
    async loadDepartment() {
      try {
        this.loadingDepart = true
        const res = await this.axios.get(DEPARTMENTS_API)
        this.departs = res.data.data
      } catch (e) {
        this.departs = []
        this.selectedDepart = null

        notifyService.fetch_error('depart', e)
      } finally {
        this.loadingDepart = false
      }
    },
    async loadPermissionList() {
      try {
        this.loadingPermissions = true
        const res = await this.axios.get(`${PERMISSION_GROUPS_API}/List`)
        this.permissionList = res.data.data
      } catch (e) {
        this.permissionList = []
        this.selectedPermission = null

        notifyService.fetch_error('permission', e)
      } finally {
        this.loadingPermissions = false
      }
    },
    async loadCompany() {
      try {
        this.loadingCompany = true
        const res = await this.axios.get(COMPANIES_API)
        this.companies = res.data.data
      } catch (e) {
        this.companies = []
        this.selectedCompany = null

        notifyService.fetch_error('company', e)
      } finally {
        this.loadingCompany = false
      }
    },
    async loadPosition() {
      try {
        this.loadingPosition = true
        const res = await this.axios.get(POSITIONS_API)
        this.positions = res.data.data
      } catch (e) {
        this.positions = []
        this.selectedPosition = null

        notifyService.fetch_error('position', e)
      } finally {
        this.loadingPosition = false
      }
    },
    async loadEmployees() {
      try {
        const api = EMPLOYEES_API
        const params = {
          depart: this.selectedDepart && this.selectedDepart.id,
          permissionGroup:
            this.selectedPermission && this.selectedPermission.id,
          company: this.selectedCompany && this.selectedCompany.id,
          status: this.selectedStatus,
          name: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingEmployee = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data
        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('employee', e)
      } finally {
        this.loadingEmployee = false
      }
    },
    onModify(item) {
      this.isNew = false
      this.selected = item
      this.isModalActive = true
    },
    onGroupsModify(item) {
      this.selected = item
      this.isPermissionGroupModalActive = true
    },
    onAreaModify(item) {
      this.selected = item
      this.isAreaModelActive = true
    },
    onAdd() {
      const today = this.$dayjs()

      this.isNew = true
      this.selected = {
        emp_code: null,
        account: null,
        password: null,
        name: null,
        english_name: null,
        company_no: 'Taipei',
        str_date: this.formatDate(today),
        email: '@fantasy-tours.com',
        status: 1,
        depart_no: '',
        position_no: '',
        is_web: 'N',
        superior: null,
        tel1: '25171157'
      }
      this.isModalActive = true
    },
    onFilter() {
      this.page = 1
      this.loadEmployees()
    },
    onFilterSearch: debounce(function() {
      this.page = 1
      this.loadEmployees()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.loadEmployees()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadEmployees()
    },
    onSend(item) {
      if (this.isNew) {
        this.createEmployee(item)
      } else {
        this.updateEmployee(item)
      }
    },
    async createEmployee(item) {
      try {
        this.sending = true
        const res = await this.axios.post(EMPLOYEES_API, item)
        this.selected = res.data.data
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadEmployees()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isModalActive = false
        this.isAreaModelActive = true
      }
    },
    async updateEmployee(item) {
      try {
        const api = `${EMPLOYEES_API}/${item.emp_code}`
        this.sending = true
        await this.axios.put(api, item)

        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadEmployees()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isModalActive = false
      }
    },
    async onUpdateGroups(emp_code, data) {
      try {
        const api = `${EMPLOYEES_API}/${emp_code}/groups`
        this.sending = true
        await this.axios.put(api, data)

        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadEmployees()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isPermissionGroupModalActive = false
      }
    },
    async onUpdateAreas(item) {
      try {
        const api = `${EMPLOYEES_API}/${item.emp_code}/areas`
        this.sending = true
        await this.axios.put(api, item)

        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadEmployees()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isAreaModelActive = false
      }
    },
    onSendMail(item) {
      this.$buefy.dialog.confirm({
        title: '是否寄信',
        message: `是否寄送帳密資訊給「${item.name}」`,
        cancelText: '取消',
        confirmText: '確定',
        type: 'is-success',
        onConfirm: () => this.sendingMail(item)
      })
    },
    async sendingMail(item) {
      try {
        const api = `${EMPLOYEES_API}/${item.emp_code}/send`
        this.sending = true
        await this.axios.get(api)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>
